import React, { useState } from "react";

// AmendmentDiff — renders an amendment detection result as a grouped diff.
//
// Props:
//   amendmentResult  { isAmendment, changes, scope, summary }
//     isAmendment: boolean
//     scope: "additive" | "reductive" | "modifying" | "none"
//     summary: human-readable summary string
//     changes: array of {
//       category: "order" | "line_added" | "line_removed" | "line_modified",
//       path:   string (dotted field path or line_item_id),
//       field:  string (label, for order-level + line_modified),
//       oldValue: any,
//       newValue: any,
//       delta:   number|undefined (numeric delta if both values are numbers)
//     }
//
// Color coding:
//   green  for additions (line_added, numeric +delta)
//   red    for removals (line_removed, numeric -delta)
//   yellow for modifications (order, line_modified)

function formatValue(v) {
  if (v == null) return "∅";
  if (typeof v === "number") return v.toLocaleString();
  if (Array.isArray(v)) return v.join(", ") || "[]";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

function formatDelta(d) {
  if (d == null) return "";
  const sign = d > 0 ? "+" : "";
  return `${sign}${d.toLocaleString()}`;
}

function ChangeRow({ change }) {
  const categoryClass =
    change.category === "line_added"
      ? "diff-add"
      : change.category === "line_removed"
        ? "diff-remove"
        : "diff-modify";

  return (
    <tr className={`diff-row ${categoryClass}`}>
      <td className="diff-path">{change.path}</td>
      <td className="diff-field">{change.field || ""}</td>
      <td className="diff-old">{formatValue(change.oldValue)}</td>
      <td className="diff-arrow">→</td>
      <td className="diff-new">{formatValue(change.newValue)}</td>
      {change.delta != null && <td className="diff-delta">{formatDelta(change.delta)}</td>}
    </tr>
  );
}

function DiffGroup({ title, changes, category }) {
  const [open, setOpen] = useState(true);
  if (!changes || changes.length === 0) return null;
  const showDelta = changes.some((c) => c.delta != null);
  return (
    <details className="diff-group" open={open} onToggle={(e) => setOpen(e.target.open)}>
      <summary className={`diff-group-summary diff-${category}`}>
        <span>{title}</span>
        <span className="diff-group-count">{changes.length}</span>
      </summary>
      <table className="diff-table">
        <thead>
          <tr>
            <th>Path</th>
            <th>Field</th>
            <th>Old</th>
            <th></th>
            <th>New</th>
            {showDelta && <th>Δ</th>}
          </tr>
        </thead>
        <tbody>
          {changes.map((c, i) => (
            <ChangeRow key={`${c.path}-${c.field}-${i}`} change={c} />
          ))}
        </tbody>
      </table>
    </details>
  );
}

/**
 * AmendmentDiff — grouped diff view for an amendment detection result.
 *
 * @param {{ amendmentResult?: object }} props
 */
export default function AmendmentDiff({ amendmentResult }) {
  if (!amendmentResult || !amendmentResult.isAmendment) {
    return (
      <div className="amendment-diff">
        <div className="diff-empty">No amendment detected — new IO.</div>
      </div>
    );
  }

  const changes = amendmentResult.changes || [];
  const order = changes.filter((c) => c.category === "order");
  const added = changes.filter((c) => c.category === "line_added");
  const removed = changes.filter((c) => c.category === "line_removed");
  const modified = changes.filter((c) => c.category === "line_modified");

  return (
    <div className="amendment-diff">
      <div className="amendment-header">
        <span className={`amendment-scope scope-${amendmentResult.scope || "none"}`}>
          {amendmentResult.scope || "none"}
        </span>
        {amendmentResult.summary && <p className="amendment-summary">{amendmentResult.summary}</p>}
      </div>
      <DiffGroup title="Order-level changes" changes={order} category="modify" />
      <DiffGroup title="Line items added" changes={added} category="add" />
      <DiffGroup title="Line items removed" changes={removed} category="remove" />
      <DiffGroup title="Line items modified" changes={modified} category="modify" />
      {changes.length === 0 && <div className="diff-empty">No changes recorded.</div>}
    </div>
  );
}